import { Calendar, Inbox } from 'lucide-react';
import { PaymentReturnNotice, readHostedCheckoutReturnState } from '@triedge/payments';
import { useMemo, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  useCancelRegistrationMutation,
  useGetParentCatalogQuery,
  useListParentRegistrationsQuery,
} from '../../../state/domainRouterApi';
import { createHostedPaymentSession } from '../../../utils/payments';
import { resolveCognitoProfile } from '../../../utils/cognito-profile';
import { mapCampRegistrationListResponse } from '../camps/viewModels';
import { ModuleSectionNav, type ModuleSectionNavItem } from '../../../components/navigation/ModuleSectionNav';
import { SupportErrorMessage } from '../../../components/overlays/EngineeringSupportBanner';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type RegistrationItem = {
  id: string;
  programId?: string;
  programName?: string;
  studentName?: string;
  sessionLabel?: string;
  startDate?: string;
  endDate?: string;
  status?: string;
  paymentStatus?: string;
  amountPaidCents?: number;
  amountDueCents: number;
  grossCents?: number;
  refundAmountCents?: number;
  stripePaymentIntentId?: string | null;
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatCurrency(cents: number | null | undefined): string {
  if (cents == null) return '$0';
  return `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

/** Statuses considered "active" for split rendering */
const ACTIVE_STATUSES = new Set([
  'active', 'confirmed', 'pending',
  'draft', 'pending_payment', 'registered', 'waitlisted',
]);

function isActiveRegistration(status: string | undefined): boolean {
  return ACTIVE_STATUSES.has(status ?? '');
}

function registrationStatusLabel(value: string | undefined): string {
  switch (value) {
    case 'active':
    case 'registered':
      return 'Registered';
    case 'confirmed':
      return 'Confirmed';
    case 'pending':
    case 'pending_payment':
      return 'Pending Payment';
    case 'draft':
      return 'Draft';
    case 'waitlisted':
      return 'Waitlisted';
    case 'cancelled':
      return 'Cancelled';
    case 'completed':
      return 'Completed';
    case 'refund_initiated':
      return 'Refund Initiated';
    case 'refunded':
      return 'Refunded';
    default:
      return value ?? '--';
  }
}

function registrationStatusDotColor(value: string | undefined): string {
  switch (value) {
    case 'active':
    case 'confirmed':
    case 'registered':
      return 'var(--aquamarine)';
    case 'pending':
    case 'pending_payment':
    case 'draft':
    case 'waitlisted':
      return 'var(--coral)';
    case 'cancelled':
    case 'refund_initiated':
    case 'refunded':
      return 'var(--strawberry)';
    case 'completed':
      return 'var(--iron-grey)';
    default:
      return 'var(--iron-grey)';
  }
}

function paymentStatusLabel(value: string | undefined): string {
  switch (value) {
    case 'paid':
      return 'Paid';
    case 'partially_paid':
      return 'Partial payment';
    case 'pending':
      return 'Processing payment';
    case 'unpaid':
      return 'Payment due';
    case 'refunded':
      return 'Refunded';
    default:
      return value ?? '--';
  }
}

function paymentStatusDotColor(value: string | undefined): string {
  switch (value) {
    case 'paid':
      return 'var(--jungle)';
    case 'partially_paid':
      return 'var(--coral)';
    case 'pending':
      return 'var(--primary)';
    case 'unpaid':
      return 'var(--strawberry)';
    case 'refunded':
      return 'var(--iron-grey)';
    default:
      return 'var(--iron-grey)';
  }
}

function isActiveCancellable(status: string | undefined): boolean {
  return ACTIVE_STATUSES.has(status ?? '');
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function LoadingSkeleton() {
  return (
    <div className="space-y-6">
      <div className="h-8 w-48 animate-pulse rounded-lg bg-gray-100" />
      <div className="h-10 w-64 animate-pulse rounded-full bg-gray-100" />
      <div className="h-16 animate-pulse rounded-xl bg-gray-100" />
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-32 animate-pulse rounded-xl bg-gray-100" />
        ))}
      </div>
    </div>
  );
}

function ErrorBanner({ title, detail }: { title: string; detail: string }) {
  return (
    <SupportErrorMessage>
      <p className="font-semibold text-irongrey">{title}</p>
      <p className="mt-1 text-sm text-irongrey/65">{detail}</p>
    </SupportErrorMessage>
  );
}

function TabNav({ active }: { active: 'catalog' | 'waitlist' | 'registrations' }) {
  const items: ModuleSectionNavItem[] = [
    { id: 'catalog', label: 'Catalog', to: '/parent/portal/camps', end: true },
    { id: 'waitlist', label: 'Waitlist', to: '/parent/portal/camps/waitlist' },
    { id: 'registrations', label: 'Registrations', to: '/parent/portal/camps/registrations' },
  ];

  return (
    <ModuleSectionNav
      items={items}
      activeId={active}
      ariaLabel="Camps navigation"
      testIds={{
        root: 'camps-tab-nav',
        itemPrefix: 'camps-tab-nav-item',
        mobileTrigger: 'camps-tab-nav-mobile-trigger',
        mobileDialog: 'camps-tab-nav-mobile-dialog',
        mobileItemPrefix: 'camps-tab-nav-mobile-item',
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// Registration Card
// ---------------------------------------------------------------------------

function RegistrationCard({
  item,
  onCancel,
  onPayNow,
  isCancelling,
  isPaying,
}: {
  item: RegistrationItem;
  onCancel: (id: string) => void;
  onPayNow: (item: RegistrationItem) => void;
  isCancelling: boolean;
  isPaying: boolean;
}) {
  const [showConfirm, setShowConfirm] = useState(false);
  const cancellable = isActiveCancellable(item.status);
  const amountDueCents = Math.max(
    0,
    Number(item.amountDueCents ?? item.grossCents ?? 0),
  );
  const payable = amountDueCents > 0 && item.paymentStatus !== 'paid' && item.paymentStatus !== 'refunded';

  return (
    <div className="rounded-xl border border-border bg-white p-5 shadow-sm">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 flex-1 space-y-1">
          <h3 className="font-semibold text-iron-grey">{item.programName ?? 'Camp Program'}</h3>
          {item.studentName ? (
            <p className="text-sm text-text-secondary">Camper: {item.studentName}</p>
          ) : null}
          {item.sessionLabel ? (
            <p className="text-sm text-text-secondary">{item.sessionLabel}</p>
          ) : null}
          {item.startDate || item.endDate ? (
            <p className="flex items-center gap-1.5 text-sm text-text-secondary">
              <Calendar className="h-3.5 w-3.5" aria-hidden="true" />
              {item.startDate ?? ''} &ndash; {item.endDate ?? ''}
            </p>
          ) : null}
        </div>

        <div className="flex flex-col items-end gap-2 sm:min-w-[180px]">
          {/* Registration status badge */}
          <div className="flex items-center gap-1.5">
            <span
              className="h-2 w-2 rounded-full"
              style={{ backgroundColor: registrationStatusDotColor(item.status) }}
            />
            <span className="text-sm font-medium text-iron-grey">
              {registrationStatusLabel(item.status)}
            </span>
          </div>

          {/* Payment status badge */}
          <div className="flex items-center gap-1.5">
            <span
              className="h-2 w-2 rounded-full"
              style={{ backgroundColor: paymentStatusDotColor(item.paymentStatus) }}
            />
            <span className="text-sm text-text-secondary">
              {paymentStatusLabel(item.paymentStatus)}
            </span>
          </div>

          {/* Amount details */}
          {amountDueCents > 0 ? (
            <p className="text-sm font-medium text-[var(--coral)]">
              {formatCurrency(amountDueCents)} due
            </p>
          ) : null}

          {payable ? (
            <button
              type="button"
              disabled={isPaying}
              onClick={() => onPayNow(item)}
              className="rounded-lg bg-[var(--primary)] px-4 py-2 text-sm font-semibold text-white transition-colors hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isPaying ? 'Redirecting…' : 'Pay Now'}
            </button>
          ) : null}

          {/* Cancel button */}
          {cancellable ? (
            <>
              {showConfirm ? (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    disabled={isCancelling}
                    onClick={() => {
                    onCancel(item.id);
                    setShowConfirm(false);
                    }}
                    className="rounded-lg bg-[var(--strawberry)] px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {isCancelling ? 'Cancelling...' : 'Confirm'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowConfirm(false)}
                    className="rounded-lg border border-border px-3 py-1.5 text-xs font-semibold text-iron-grey transition-colors hover:bg-gray-50"
                  >
                    Keep
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowConfirm(true)}
                  className="rounded-lg border border-[var(--strawberry)] px-4 py-2 text-sm font-semibold text-[var(--strawberry)] transition-colors hover:bg-[var(--strawberry)]/5"
                >
                  Cancel Registration
                </button>
              )}
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page Component
// ---------------------------------------------------------------------------

export function ParentCampsRegistrationsPage() {
  const location = useLocation();
  const { data: rawData, isLoading, isError, error } = useListParentRegistrationsQuery();
  const { data: rawCatalog } = useGetParentCatalogQuery();
  const [cancelRegistration] = useCancelRegistrationMutation();
  const hostedReturnState = useMemo(
    () => readHostedCheckoutReturnState(location.search),
    [location.search],
  );
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [payingId, setPayingId] = useState<string | null>(null);
  const data = mapCampRegistrationListResponse(rawData, rawCatalog);
  const items = data.items as RegistrationItem[];
  const totalCount = data.totalCount;

  const activeRegistrations = useMemo(
    () => items.filter((registration) => isActiveRegistration(registration.status)),
    [items],
  );
  const historicalRegistrations = useMemo(
    () => items.filter((registration) => !isActiveRegistration(registration.status)),
    [items],
  );

  const financialSummary = useMemo(() => {
    const totalGrossCents = items.reduce((sum, registration) => sum + (registration.grossCents ?? 0), 0);
    const totalPaidCents = items.reduce((sum, registration) => sum + (registration.amountPaidCents ?? 0), 0);
    const totalRefundedCents = items.reduce((sum, registration) => sum + (registration.refundAmountCents ?? 0), 0);
    const balanceDueCents = totalGrossCents - totalPaidCents;
    return { totalGrossCents, totalPaidCents, totalRefundedCents, balanceDueCents };
  }, [items]);

  const handleCancel = async (registrationId: string) => {
    setCancellingId(registrationId);
    try {
      await cancelRegistration({ registrationId }).unwrap();
    } catch {
      // Error surfaces via cache invalidation / refetch
    } finally {
      setCancellingId(null);
    }
  };

  const handlePayNow = async (item: RegistrationItem) => {
    const registrationId = String(item.id ?? '').trim();
    if (!registrationId) {
      return;
    }
    setPayingId(registrationId);
    try {
      const profile = resolveCognitoProfile();
      const amountDueCents = Math.max(
        0,
        Number(item.amountDueCents ?? item.grossCents ?? 0),
      );
      if (amountDueCents <= 0) {
        return;
      }
      const checkoutUrl = await createHostedPaymentSession({
        moduleId: 'camps',
        operationKey: 'camps_balance_checkout',
        mode: 'payment',
        currency: 'usd',
        successUrl: `${window.location.origin}/parent/portal/camps/registrations?checkout=success`,
        cancelUrl: `${window.location.origin}/parent/portal/camps/registrations?checkout=cancel`,
        customerEmail: profile?.email,
        customerReferenceId: profile?.userSub,
        description: `${item.programName ?? 'Camp'} registration checkout`,
        lineItems: [{
          name: `${item.programName ?? 'Camp'} • ${item.studentName ?? 'Camper'}`,
          description: 'Camp registration payment',
          quantity: 1,
          unitAmountCents: amountDueCents,
        }],
        metadata: {
          route: '/parent/portal/camps/registrations',
          registration_ids: registrationId,
          program_id: item.programId ?? '',
          actorSub: profile?.userSub ?? '',
        },
      });
      window.location.assign(checkoutUrl);
    } catch (paymentError) {
      console.error('[ParentCampsRegistrationsPage] Failed to start camps checkout', paymentError);
    } finally {
      setPayingId(null);
    }
  };

  if (isLoading) {
    return (
      <main
        data-testid="parent-camps-registrations"
        data-route-ready="true"
        data-extracted-from="source-faithful:parent-camps-v1.0"
        className="bg-background-light"
      >
        <div className="w-full space-y-6">
          <PaymentReturnNotice
            state={hostedReturnState}
            successTitle="Camp payment handed off successfully"
            successBody="Stripe returned successfully. Registration payment state will refresh as the camp records reconcile."
            cancelTitle="Camp payment was not completed"
            cancelBody="No camp payment was finalized. You can retry from the outstanding registrations list."
            guidanceBody="Registration balances update from the camp payment read model after reconciliation. If a balance still looks open, refresh this list after the payment event settles."
          />
          <LoadingSkeleton />
        </div>
      </main>
    );
  }

  if (isError) {
    return (
      <main
        data-testid="parent-camps-registrations"
        data-route-ready="true"
        data-extracted-from="source-faithful:parent-camps-v1.0"
        className="bg-background-light"
      >
        <div className="w-full space-y-6">
          <PaymentReturnNotice
            state={hostedReturnState}
            successTitle="Camp payment handed off successfully"
            successBody="Stripe returned successfully. Registration payment state will refresh as the camp records reconcile."
            cancelTitle="Camp payment was not completed"
            cancelBody="No camp payment was finalized. You can retry from the outstanding registrations list."
            guidanceBody="Registration balances update from the camp payment read model after reconciliation. If a balance still looks open, refresh this list after the payment event settles."
          />
          <ErrorBanner
            title="Unable to load registrations"
            detail={
              (error && typeof error === 'object' && 'message' in error
                ? String((error as { message?: unknown }).message)
                : null) ??
              'Registration data is not available right now. Please try again later.'
            }
          />
        </div>
      </main>
    );
  }

  return (
    <main
      data-testid="parent-camps-registrations"
      data-route-ready="true"
      data-extracted-from="source-faithful:parent-camps-v1.0"
      className="bg-background-light"
    >
      <div className="w-full space-y-6">
        <PaymentReturnNotice
          state={hostedReturnState}
          noticeKey="parent-camps-registrations"
          successTitle="Camp payment handed off successfully"
          successBody="Stripe returned successfully. Registration payment state will refresh as the camp records reconcile."
          cancelTitle="Camp payment was not completed"
          cancelBody="No camp payment was finalized. You can retry from the outstanding registrations list."
          guidanceBody="Registration balances update from the camp payment read model after reconciliation. If a balance still looks open, refresh this list after the payment event settles."
        />
        {/* Page header */}
        <div data-testid="camps-registrations-header">
          <p className="text-xs font-semibold uppercase tracking-[0.08em] text-[var(--primary)]">
            Parent portal
          </p>
          <h1 className="text-[24px] font-semibold text-iron-grey">Camp Registrations</h1>
        </div>

        {/* Tab navigation */}
        <TabNav active="registrations" />

        {/* Summary row */}
        <div
          data-testid="camps-registrations-summary"
          className="rounded-xl border border-border bg-white p-5 shadow-sm"
        >
          <div className="flex flex-wrap items-center gap-x-8 gap-y-2">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.05em] text-text-secondary">
                Total registrations
              </p>
              <p className="text-lg font-semibold text-iron-grey">{totalCount}</p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.05em] text-text-secondary">
                Active
              </p>
              <p className="text-lg font-semibold text-iron-grey">{activeRegistrations.length}</p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.05em] text-text-secondary">
                Past
              </p>
              <p className="text-lg font-semibold text-iron-grey">{historicalRegistrations.length}</p>
            </div>
          </div>
        </div>

        {/* Gap 7: Financial summary */}
        <div
          data-testid="camps-registrations-financial-summary"
          className="source-faithful-kpiGrid grid grid-cols-2 gap-4 sm:grid-cols-4"
        >
          <div className="rounded-xl border border-border bg-white p-5 shadow-sm">
            <div className="mb-3 flex items-center gap-2">
              <p className="text-xs font-semibold uppercase tracking-[0.05em] text-text-secondary">Total Cost</p>
            </div>
            <p className="text-[32px] font-semibold text-iron-grey">
              {formatCurrency(financialSummary.totalGrossCents)}
            </p>
          </div>
          <div className="rounded-xl border border-border bg-white p-5 shadow-sm">
            <div className="mb-3 flex items-center gap-2">
              <span className="h-2 w-2 rounded-full" style={{ backgroundColor: 'var(--aquamarine)' }} />
              <p className="text-xs font-semibold uppercase tracking-[0.05em] text-text-secondary">Paid to Date</p>
            </div>
            <p className="text-[32px] font-semibold text-iron-grey">
              {formatCurrency(financialSummary.totalPaidCents)}
            </p>
          </div>
          <div className="rounded-xl border border-border bg-white p-5 shadow-sm">
            <div className="mb-3 flex items-center gap-2">
              {financialSummary.balanceDueCents > 0 ? (
                <span className="h-2 w-2 rounded-full" style={{ backgroundColor: 'var(--coral)' }} />
              ) : null}
              <p className="text-xs font-semibold uppercase tracking-[0.05em] text-text-secondary">Balance Due</p>
            </div>
            <p className="text-[32px] font-semibold text-iron-grey">
              {formatCurrency(financialSummary.balanceDueCents)}
            </p>
          </div>
          <div className="rounded-xl border border-border bg-white p-5 shadow-sm">
            <div className="mb-3 flex items-center gap-2">
              <p className="text-xs font-semibold uppercase tracking-[0.05em] text-text-secondary">Refunded</p>
            </div>
            <p className="text-[32px] font-semibold text-iron-grey">
              {formatCurrency(financialSummary.totalRefundedCents)}
            </p>
          </div>
        </div>

        {/* Registration list or empty state */}
        {items.length === 0 ? (
          <div
            data-testid="camps-registrations-empty"
            className="rounded-xl border border-border bg-white py-16 text-center shadow-sm"
          >
            <Inbox className="mx-auto h-10 w-10 text-text-secondary" aria-hidden="true" />
            <p className="mt-4 font-semibold text-iron-grey">No registrations yet</p>
            <p className="mt-1 text-sm text-text-secondary">
              Browse the{' '}
              <NavLink to="/parent/portal/camps" className="font-medium text-[var(--primary)] hover:underline">
                catalog
              </NavLink>{' '}
              to register your children for camp programs.
            </p>
          </div>
        ) : (
          <>
            {/* Gap 6: Active Registrations section */}
            <div data-testid="camps-registrations-active">
              <h2 className="mb-3 text-sm font-semibold text-iron-grey">
                Active ({activeRegistrations.length})
              </h2>
              {activeRegistrations.length === 0 ? (
                <div className="rounded-xl border border-border bg-white py-10 text-center shadow-sm">
                  <p className="text-sm text-text-secondary">No active registrations.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {activeRegistrations.map((item) => (
                    <RegistrationCard
                      key={item.id}
                      item={item}
                      onCancel={(id) => { void handleCancel(id); }}
                      onPayNow={(registration) => { void handlePayNow(registration); }}
                      isCancelling={cancellingId === item.id}
                      isPaying={payingId === item.id}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Gap 6: Past Registrations section */}
            <div data-testid="camps-registrations-historical">
              <h2 className="mb-3 text-sm font-semibold text-iron-grey">
                Past ({historicalRegistrations.length})
              </h2>
              {historicalRegistrations.length === 0 ? (
                <div className="rounded-xl border border-border bg-white py-10 text-center shadow-sm">
                  <p className="text-sm text-text-secondary">No past registrations.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {historicalRegistrations.map((item) => (
                    <RegistrationCard
                      key={item.id}
                      item={item}
                      onCancel={(id) => { void handleCancel(id); }}
                      onPayNow={(registration) => { void handlePayNow(registration); }}
                      isCancelling={cancellingId === item.id}
                      isPaying={payingId === item.id}
                    />
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </main>
  );
}
